import { motion, HTMLMotionProps } from "framer-motion"
import { cn } from "@/lib/utils"

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger"
type ButtonSize = "sm" | "md" | "lg"

interface GlassButtonProps
  extends Omit<HTMLMotionProps<"button">, "children" | "type"> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  disabled?: boolean
  children?: React.ReactNode
  className?: string
  onClick?: () => void
  type?: "button" | "submit" | "reset"
  icon?: React.ReactNode
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-[var(--accent-blue)] text-white border-transparent hover:brightness-110 shadow-lg shadow-blue-500/25",
  secondary:
    "backdrop-blur-[20px] bg-glass-bg border border-glass-border text-[var(--text-primary)]",
  ghost:
    "bg-transparent border-transparent text-[var(--text-primary)] hover:backdrop-blur-[16px] hover:bg-glass-hover",
  danger:
    "bg-red-500 text-white border-transparent hover:brightness-110 shadow-lg shadow-red-500/25",
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-xs gap-1.5 rounded-lg",
  md: "h-11 px-4 text-sm gap-2 rounded-xl",
  lg: "h-12 px-6 text-base gap-2.5 rounded-xl",
}

function Spinner({ size }: { size: ButtonSize }) {
  const dim = size === "sm" ? 12 : size === "md" ? 14 : 16
  return (
    <motion.svg
      width={dim}
      height={dim}
      viewBox="0 0 16 16"
      fill="none"
      animate={{ rotate: 360 }}
      transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
      aria-hidden="true"
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="2"
      />
      <path
        d="M8 2a6 6 0 016 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </motion.svg>
  )
}

export function GlassButton({
  variant = "primary",
  size = "md",
  loading = false,
  disabled = false,
  children,
  className,
  onClick,
  type = "button",
  icon,
  ...motionProps
}: GlassButtonProps) {
  const isDisabled = disabled || loading

  return (
    <motion.button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      className={cn(
        "relative inline-flex items-center justify-center font-medium border transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-blue)] focus-visible:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      whileHover={
        !isDisabled
          ? {
              y: variant === "secondary" ? -1 : 0,
              scale: 1.02,
              transition: { duration: 0.15, ease: "easeOut" },
            }
          : undefined
      }
      whileTap={
        !isDisabled
          ? {
              scale: 0.97,
              transition: { duration: 0.08 },
            }
          : undefined
      }
      {...motionProps}
    >
      {loading ? (
        <>
          <Spinner size={size} />
          <span className="opacity-70">{children}</span>
        </>
      ) : (
        <>
          {icon && <span className="shrink-0">{icon}</span>}
          {children}
        </>
      )}
    </motion.button>
  )
}
